import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';
import { Suspense, lazy } from 'react';
const ThreeScene = lazy(() => import('./ThreeScene'));

function useLenisProgress(ref) {
  const [progress, setProgress] = useState(0);

  const update = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const p = Math.max(0, Math.min(1, -rect.top / rect.height));
    setProgress(p);
  }, [ref]);

  useEffect(() => {
    const lenis = window.__lenis;
    if (lenis) {
      lenis.on('scroll', update);
      update();
      return () => lenis.off('scroll', update);
    }
    const handler = () => requestAnimationFrame(update);
    window.addEventListener('scroll', handler, { passive: true });
    update();
    return () => window.removeEventListener('scroll', handler);
  }, [update]);

  return progress;
}

function ScrollSlide({ children, progress, delay = 0 }) {
  const adjusted = Math.max(0, (progress - delay) / (1 - delay));
  const x = -(adjusted * 120);
  const opacity = Math.max(0, 1 - adjusted * 2.5);
  return (
    <div style={{ transform: `translate3d(${x}%, 0, 0)`, opacity }}>
      {children}
    </div>
  );
}

export default function Hero() {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const progress = useLenisProgress(sectionRef);
  const starX = progress * 130;

  // NUEVO: Detector de dispositivo móvil para bloquear el motor 3D
  const [isMobile, setIsMobile] = useState(true);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    // Comprobar al cargar
    checkMobile();
    // Comprobar si se redimensiona la pantalla
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <section
      ref={sectionRef}
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex flex-col overflow-hidden"
      style={{ background: '#050505' }}
    >
      {/* NUEVO: Solo renderizamos el ThreeScene si NO es móvil */}
      {!isMobile && (
        <Suspense fallback={null}>
          <ThreeScene />
        </Suspense>
      )}
      
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-transparent via-transparent to-[#050505]" />

      {/* MOBILE */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 md:hidden">

        <motion.h1
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="font-heading text-5xl sm:text-6xl font-bold text-white tracking-tighter leading-[0.9] text-center"
        >
          <span>{t.hero.headlineL1} </span>
          {t.hero.headlineRed && <span className="text-[#e14141]">{t.hero.headlineRed} </span>}
          <span>{t.hero.headlineL2}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="font-body text-base text-zinc-400 mt-8 max-w-md text-center leading-relaxed"
        >
          {t.hero.subheadline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-10"
        >
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            aria-label="Ir a la sección de contacto"
            className="font-body text-sm bg-[#d32f2f] text-white px-8 py-3.5 rounded-full hover:bg-[#d33333] transition-all duration-300"
          >
            {t.hero.cta}
          </button>
        </motion.div>
      </div>

      {/* DESKTOP */}
      <div className="relative z-10 flex-1 hidden md:flex flex-col justify-between px-12 lg:px-24 pt-32 pb-10">
        <div>
          <motion.h1
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-heading font-bold text-white tracking-tighter leading-[0.85] text-left"
            style={{ fontSize: 'clamp(4rem, 9.5vw, 10rem)' }}
            data-testid="hero-headline"
          >
            <ScrollSlide progress={progress} delay={0}>
              <span className="block">{t.hero.headlineL1}</span>
            </ScrollSlide>
            {t.hero.headlineRed && (
              <ScrollSlide progress={progress} delay={0.04}>
                <span className="block text-[#e14141]">{t.hero.headlineRed}</span>
              </ScrollSlide>
            )}
            <ScrollSlide progress={progress} delay={t.hero.headlineRed ? 0.08 : 0.04}>
              <span className="block">{t.hero.headlineL2}</span>
            </ScrollSlide>
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <ScrollSlide progress={progress} delay={0.12}>
              <div className="flex items-center gap-5 mt-8">
                <button
                  onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                  data-testid="hero-cta-button"
                  aria-label="Ir a la sección de contacto"
                  className="font-body text-sm bg-[#d32f2f] text-white px-8 py-3 rounded-full hover:bg-[#d33333] transition-all duration-300 hover:scale-105"
                >
                  {t.hero.cta}
                </button>
              </div>
            </ScrollSlide>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <ScrollSlide progress={progress} delay={0.15}>
            <p
              className="font-body text-base lg:text-lg text-white max-w-[45%] lg:max-w-xl leading-relaxed text-left"
              data-testid="hero-subheadline"
            >
              {t.hero.subheadline}
            </p>
          </ScrollSlide>
        </motion.div>
      </div>

      {/* Star */}
      <div
        className="absolute bottom-[10%] md:bottom-[5%] right-12 lg:right-24 z-[2] pointer-events-none"
        data-testid="hero-star"
        style={{ transform: `translate3d(${starX}%, 0, 0)` }}
      >
        <motion.img
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          src="/ICONFIRST.avif"
          alt="Estrella First Studio"
          width="520"
          height="520"
          className="w-[150px] md:w-[400px] lg:w-[520px] h-auto opacity-90"
        />
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ArrowDown className="text-zinc-500" size={20} aria-label="Desplazarse hacia abajo" />
        </motion.div>
      </motion.div>
    </section>
  );
}
